<!--
	LSP-19649: deliberately NO `@click` or `@click:close` binding on the chip. This component's root IS the v-chip and
	`inheritAttrs` is on, so a parent's `@click` already reaches Vuetify as a fallthrough listener —
	and Vuetify paints the pointer cursor, ripple and tabindex="0" purely from `hasEvent(attrs, "click")`.
	Binding a click here unconditionally made every chip in the app advertise a click that did nothing.
	Both bindings also double-fired: the parent's handler ran once via fallthrough and again via the
	re-emit, so removing a filter pill emitted `filter-tags` twice per click.
-->
<template>
	<v-chip
		:close="closable"
		:closable="closable"
		variant="tonal"
		close-icon="icon-close-small"
		:color="getBackgroundColor"
		:title="label?.name"
		:size="small ? 'small' : 'default'">
		<span class="text-truncate min-w-0 m-w-100">{{ label?.name ?? "" }}</span>
		<v-tooltip v-if="exceedsMaxLength" location="top">
			<template #activator="{ props: tooltipProps }">
				<v-icon v-bind="tooltipProps" size="x-small" color="warning" class="ms-1">icon-warning-triangle</v-icon>
			</template>
			<span>This tag exceeds the {{ TAG_MAX_LENGTH }}-character limit</span>
		</v-tooltip>
	</v-chip>
</template>
<script lang="ts">
	import { getRandomColor } from "@/helpers/tags";
	import { countCharacters } from "@/helpers/utilities";
	import { TAG_MAX_LENGTH } from "@/api/constants";
	export default {
		components: {},
		props: {
			label: {
				type: Object,
				required: true
			},
			closable: {
				type: Boolean,
				default: false
			},
			small: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				TAG_MAX_LENGTH
			};
		},
		computed: {
			getBackgroundColor() {
				if (this.label.baseColor) {
					return this.label.baseColor;
				}
				const hexColor = getRandomColor();
				// eslint-disable-next-line vue/no-side-effects-in-computed-properties
				this.label.baseColor = hexColor;
				return hexColor;
			},
			exceedsMaxLength(): boolean {
				return this.label?.name ? countCharacters(this.label.name) > TAG_MAX_LENGTH : false;
			}
		}
	};
</script>

<style scoped>
	.v-chip {
		/* Truncate down to a narrow container (e.g. the ~150px project-table Tags column) while keeping the original 200px ceiling in unbounded containers, so this stays a superset of the prior cap. */
		max-width: min(200px, 100%);
	}

	:deep(.v-chip__content) {
		/* min-width:0 lets the flex content area shrink below its intrinsic width so the label's text-truncate ellipsis engages; the label span itself shrinks via the global min-w-0 / m-w-100 utilities. */
		min-width: 0;
		overflow: hidden;
	}
</style>
